import React from "react";
import { CheckCircle2, Circle, Clock, AlertCircle } from "lucide-react";

const PHASES = ["Getting Started", "Building the Claim", "With the VA", "Outcome"];

const STAGE_PHASE = {
  "Intake Form Submitted": "Getting Started",
  "Free Consultation Scheduled": "Getting Started",
  "Consultation Complete / Accepted as Client": "Getting Started",
  "Gathering Documentation": "Building the Claim",
  "Waiting on Nexus Letter": "Building the Claim",
  "Claim Package Ready for Review": "Building the Claim",
  "Claim Submitted": "With the VA",
  "VA Reviewing / Evidence Gathering": "With the VA",
  "C&P Exam Scheduled": "With the VA",
  "C&P Exam Completed": "With the VA",
  "Decision Pending": "With the VA",
  "Decision Received": "With the VA",
  "Approved / Rating Received": "Outcome",
  "Partially Approved": "Outcome",
  "Denied": "Outcome",
};

const OUTCOME_STAGES = ["Approved / Rating Received", "Partially Approved", "Denied"];

export default function ClaimTracker({ claim }) {
  if (!claim) return null;
  const stages = claim.applicable_stages || [];
  const currentIdx = stages.indexOf(claim.current_status);

  // For outcome phase, we only show the actual outcome the admin selected (not all three)
  const visible = stages.filter((s) => {
    if (!OUTCOME_STAGES.includes(s)) return true;
    return s === claim.current_status;
  });

  const grouped = PHASES.map((p) => ({
    phase: p,
    stages: visible.filter((s) => STAGE_PHASE[s] === p),
  })).filter((g) => g.stages.length > 0);

  return (
    <div data-testid={`claim-tracker-${claim.claim_id}`}>
      {/* Waiting-on + note + C&P */}
      {(claim.status_note || claim.waiting_on || claim.cp_exam_date) && (
        <div className="mb-6 bg-secondary p-5 border-l-4 border-accent">
          {claim.waiting_on && (
            <div className="flex items-center gap-2 mb-2" data-testid="claim-waiting-on">
              <AlertCircle className="w-4 h-4 text-primary" />
              <span className="font-semibold text-sm">Waiting on: {claim.waiting_on === "Veteran" ? "You" : claim.waiting_on}</span>
            </div>
          )}
          {claim.cp_exam_date && (
            <div className="flex items-center gap-2 mb-2" data-testid="claim-cp-date">
              <Clock className="w-4 h-4 text-primary" />
              <span className="font-semibold text-sm">C&P Exam: {new Date(claim.cp_exam_date).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</span>
            </div>
          )}
          {claim.status_note && (
            <p className="text-[15px] text-primary/85 leading-relaxed mt-1" data-testid="claim-note">{claim.status_note}</p>
          )}
        </div>
      )}

      {/* Timeline */}
      <div className="space-y-8">
        {grouped.map((g) => (
          <div key={g.phase}>
            <div className="label-caps text-primary/70 mb-3">{g.phase}</div>
            <ol className="relative border-l-2 border-border pl-6 space-y-4">
              {g.stages.map((s) => {
                const idx = stages.indexOf(s);
                const isCurrent = s === claim.current_status;
                const isDone = idx >= 0 && idx < currentIdx && !OUTCOME_STAGES.includes(s);
                const Icon = isCurrent ? Clock : isDone ? CheckCircle2 : Circle;
                return (
                  <li key={s} className="relative" data-testid={`stage-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    <span className={`absolute -left-[34px] top-1 grid place-items-center w-6 h-6 rounded-full ${
                      isCurrent ? "bg-accent text-accent-foreground" :
                      isDone ? "bg-primary text-primary-foreground" :
                      "bg-white border-2 border-border text-primary/30"
                    }`}>
                      <Icon className="w-3.5 h-3.5" strokeWidth={isCurrent ? 2.5 : 2} />
                    </span>
                    <div className={`${isCurrent ? "font-semibold text-primary" : isDone ? "text-primary/60 line-through" : "text-primary/50"} text-[15px]`}>
                      {s}
                      {isCurrent && <span className="ml-2 inline-block text-[10px] font-mono uppercase tracking-widest bg-accent text-accent-foreground px-2 py-0.5">Current</span>}
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        ))}
      </div>
    </div>
  );
}
